import { Box, Divider, Grid } from "@mui/material";
import Typography from "@mui/material/Typography";
import { useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import useAPI from "../../../../../../api/hooks/useAPI";
import useAssignUserManagement from "../../../../../../api/hooks/useAssignUserManagement";
import { useCheckUserPermission } from "../../../../../../api/hooks/useCheckUserPermission";
import useColors from "../../../../../../api/hooks/useColors";
import useTicketingIntegrations from "../../../../../../api/hooks/useTicketingIntegrations";
import AssigneeSelector from "../../../../../../components/assignee/AssigneeSelector";
import { CustomerFeatureFlagsContext } from "../../../../../../contexts/CustomerFeatureFlagsContext";
import {
  ControlStatus,
  DRAWER_TABS,
  PLATFORM_USER_ACTIONS,
} from "../../../../../../utils/constants";
import AsanaInteractionManager from "../../../../../stack/integrations/Asana/AsanaInteractionManager";
import JiraInteractionManager from "../../../../../stack/integrations/Jira/JiraInteractionManager";
import ServiceNowInteractionManager from "../../../../../stack/integrations/ServiceNow/ServiceNowInteractionManager";
import DetectionsAccordion from "../../../../detections/components/DetectionsAccordion";
import { getActiveDetections } from "../../../../detections/components/utils";
import AssetsSection from "../../AssetsSection/AssetsSection";
import ControlEntitiesSection from "../../ControlMisconfiguration/ControlEntitiesSection";
import { getControlEntityIds } from "../../EntitiesSection/utils";
import RecentlyFoundLabel, { isRecentlyFound } from "../../RecentlyFoundLabel";
import ScanMenuButton from "../../ScanMenuButton";
import SnoozeButton from "../../SnoozeButton/SnoozeButton";
import { SnoozeType } from "../../SnoozeButton/constants";
import ControlDetailsSection from "./ControlDetailsSection";
import ControlFavoritesButton from "./ControlFavoritesButton";
import ControlOverviewSection from "./ControlOverviewSection";
import ControlRemediateSection from "./ControlRemediateSection";
import CopyLinkButton from "../CopyLinkButton";

export const HeaderControlSection = ({ fullControl }) => {
  return (
    <Grid container direction="row" spacing={2} pt={3}>
      <Grid item xs={12}>
        <Grid container direction="row">
          <Grid
            item
            xs={10}
            display={"flex"}
            justifyContent={"flex-start"}
            gap={2}
          >
            <Typography variant="h5">
              {fullControl?.name_for_gap_state}
            </Typography>{" "}
            {isRecentlyFound(fullControl.first_seen?.timestamp_utc) && (
              <RecentlyFoundLabel
                foundTimestamp={fullControl.first_seen?.timestamp_utc || ""}
              />
            )}
          </Grid>

          <Grid item xs={2} display={"flex"} justifyContent={"flex-end"}>
            <Typography variant="h5">#{fullControl?.control_id}</Typography>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};

const ControlTab = ({
  fullControl,
  setActiveTab,
  demoMode,
  assetAffectedByDetection = [],
  scrollableContainerRef,
}) => {
  const [isScanLoading, setIsScanLoading] = useState(false);
  const [showDetailedAffectedEntities, setShowDetailedAffectedEntities] =
    useState(false);

  const { detectionsActive } = useContext(CustomerFeatureFlagsContext);
  const {
    usersMap,
    handleAssignControlAndRemediation,
    isControlRemediationLoading,
  } = useAssignUserManagement();
  const { trackEvent } = useAPI();
  const { permitted: hasPermission } = useCheckUserPermission(
    PLATFORM_USER_ACTIONS.ASSIGN_CONTROL_TO_USER
  );
  const colors = useColors();
  const navigate = useNavigate();
  const { isJiraIntegrated, isAsanaIntegrated, isServiceNowIntegrated } =
    useTicketingIntegrations();

  const handleGoToDetection = () => {
    setActiveTab(DRAWER_TABS.DETECTION_TAB);
  };

  const viewRelatedScenarios = () => {
    const controlID = fullControl?.control_id;
    trackEvent(
      "User clicked on View Related Scenarios from Control Drawer for Control id " +
        controlID
    );
    navigate("/posture/surface", {
      state: { controlID: controlID },
    });
  };

  const controlId = fullControl?.control_id;
  const selectedAssignee = usersMap.get(fullControl?.assignee_email) || null;
  const handleAssigneeChange = (assignee) => {
    handleAssignControlAndRemediation(fullControl?.control_id, assignee);
  };

  const scrollDownToEntitiesSection = () => {
    if (scrollableContainerRef.current) {
      scrollableContainerRef.current.scrollTo({
        top: scrollableContainerRef.current.scrollTop + 290,
        behavior: "smooth",
      });
    }
  };

  return (
    <Grid container direction="row" alignItems={"center"} gap={3} pb={1}>
      <Grid
        item
        xs={12}
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 1,
          backgroundColor: colors.grey_50,
          boxShadow: `0px -3px 0px 3px ${colors.grey_50}`,
        }}
      >
        <Grid container direction="column" gap={2}>
          <HeaderControlSection fullControl={fullControl} />
          <Grid container direction="row" alignItems={"center"} gap={1} pb={1}>
            <Grid
              item
              xs={12}
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 2,
              }}
            >
              <Grid item xs={12}>
                <ControlDetailsSection
                  fullControl={fullControl}
                  demoMode={demoMode}
                />
              </Grid>
              {!showDetailedAffectedEntities && (
                <Grid
                  container
                  direction="row"
                  spacing={2}
                  alignItems={"center"}
                >
                  <Grid
                    container
                    direction="column"
                    item
                    xs={12}
                    justifyContent="space-between"
                    gap={2}
                  >
                    <Grid
                      item
                      sx={{
                        display: "flex",
                        flexDirection: "column",

                        gap: 1,
                      }}
                    >
                      <Box>
                        <Typography variant="subtitle2">
                          Quick Actions
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "row",
                          justifyContent: "space-between",
                          alignItems: "center",
                          gap: 1,
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "row",
                            gap: 1,
                          }}
                        >
                          <Grid item>
                            <ScanMenuButton
                              isScanLoading={isScanLoading}
                              setLoading={setIsScanLoading}
                              loadingMessage={
                                "Analyzing systems for this control..."
                              }
                              gapIdsToScan={[fullControl?.control_id]}
                              gapIdsToSnooze={[]}
                              tooltipText={"Scan this control"}
                            />
                          </Grid>{" "}
                          {(fullControl?.is_gap || fullControl?.is_snoozed) && (
                            <Grid item>
                              <SnoozeButton
                                controlIds={[fullControl?.control_id]}
                                snoozeType={SnoozeType.CONTROL}
                                initialIsSnooze={fullControl?.is_snoozed}
                                size="medium"
                                fromDrawer={true}
                                hideTitle={true}
                              />
                            </Grid>
                          )}
                          <Grid item>
                            <AssigneeSelector
                              value={selectedAssignee}
                              onChange={handleAssigneeChange}
                              size="medium"
                              hideLabel={true}
                              loading={isControlRemediationLoading(controlId)}
                              hasPermission={hasPermission}
                            />
                          </Grid>
                          {isJiraIntegrated && (
                            <Grid item>
                              <JiraInteractionManager
                                fullControl={fullControl}
                                sx={{ height: "32px" }}
                                hideTitle={true}
                              />
                            </Grid>
                          )}
                          {isAsanaIntegrated && (
                            <Grid item>
                              <AsanaInteractionManager
                                fullControl={fullControl}
                                sx={{ height: "32px" }}
                                hideTitle={true}
                              />
                            </Grid>
                          )}
                          {isServiceNowIntegrated && (
                            <Grid item>
                              <ServiceNowInteractionManager
                                fullControl={fullControl}
                                sx={{ height: "32px" }}
                                hideTitle={true}
                              />
                            </Grid>
                          )}
                          <Grid item>
                            <ControlFavoritesButton
                              controlId={fullControl?.control_id}
                            />
                          </Grid>
                          <Grid item>
                            <CopyLinkButton
                              controlId={fullControl?.control_id}
                            />
                          </Grid>
                        </Box>
                        <Box>
                          <Typography
                            variant="caption"
                            onClick={viewRelatedScenarios}
                            sx={{
                              cursor: "pointer",
                              color: colors.primary_main,
                            }}
                          >
                            {"View Related Scenarios →"}
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                  </Grid>
                </Grid>
              )}
            </Grid>
          </Grid>
        </Grid>
        <Divider sx={{ width: "100%", marginTop: "4px" }} />
      </Grid>

      {detectionsActive &&
        fullControl?.is_detected &&
        !showDetailedAffectedEntities && (
          <DetectionsAccordion
            detections={getActiveDetections(fullControl?.last_detections)}
            source={"control"}
            handleOnClick={handleGoToDetection}
            assetAffectedByDetection={assetAffectedByDetection}
          />
        )}
      {!showDetailedAffectedEntities && (
        <>
          <Grid item xs={12}>
            <ControlOverviewSection fullControl={fullControl} />
          </Grid>
          <Grid item xs={12}>
            <AssetsSection
              entity_ids={getControlEntityIds(fullControl)}
              source="gap"
            />
          </Grid>
        </>
      )}
      {fullControl.status !== ControlStatus.COMPLIANT && (
        <ControlEntitiesSection
          fullControl={fullControl}
          showDetailedAffectedEntities={showDetailedAffectedEntities}
          setShowDetailedAffectedEntities={setShowDetailedAffectedEntities}
          scrollDown={scrollDownToEntitiesSection}
        />
      )}

      {!showDetailedAffectedEntities && (
        <Grid item xs={12}>
          <ControlRemediateSection setTab={setActiveTab} />
        </Grid>
      )}
    </Grid>
  );
};

export default ControlTab;
